import React from "react";
import { Typography, Box, CardContent, Button } from "@mui/material";
import type { MateriomFormulationSource } from "../../types/frontend";
import { sourcesPanelStyles } from "../PanelStyles";

interface MateriomFormulationCardContentProps {
  source: MateriomFormulationSource;
  onLinkClick: (e: React.MouseEvent, source: MateriomFormulationSource) => void;
}

/**
 * Formats the ingredient list to display as "ingredient (concentration unit), ingredient (concentration unit), etc."
 */
const formatIngredients = (source: MateriomFormulationSource): string => {
  if (!source.ingredients || source.ingredients.length === 0) {
    return "No ingredients specified";
  }

  const formattedIngredients = source.ingredients.map((ingredient, index) => {
    const concentration = source.ingredient_concentrations[index];
    const unit = source.ingredient_units[index];

    // Account for cases where the concentration or unit is not specified
    const concStr = concentration !== null ? `${concentration}` : "";
    const unitStr = unit !== null ? `${unit}` : "";

    let concUnitStr = "";
    if (concStr) {
      concUnitStr = ` (${concStr})`;
    }
    if (unitStr) {
      concUnitStr = ` (${concStr} ${unitStr})`;
    }
    return `${ingredient}${concUnitStr}`;
  });

  return formattedIngredients.join(", ");
};

/**
 * Formats the author or contributor list to display as "author (last name), author (last name), etc."
 */
const formatAuthorsOrContributors = (
  source: MateriomFormulationSource
): string => {
  const formatNamesWithLastNames = (
    names: string[],
    lastNames: string[] | null
  ): string[] => {
    return names.map((name, index) => {
      const lastName = lastNames ? lastNames[index] : null;
      // account for cases where the last name is in the name
      if (lastName && name.includes(lastName)) {
        return name;
      }
      return lastName ? `${name} ${lastName}` : name;
    });
  };

  if (source.contributor_names && source.contributor_names.length > 0) {
    const contributors = formatNamesWithLastNames(
      source.contributor_names,
      source.contributor_last_names
    );
    return contributors.join(", ");
  }
  if (source.author_names && source.author_names.length > 0) {
    const authors = formatNamesWithLastNames(
      source.author_names,
      source.author_last_names
    );
    return authors.join(", ");
  }

  return "No authors or contributors specified";
};

const MateriomFormulationCardContent: React.FC<
  MateriomFormulationCardContentProps
> = ({ source, onLinkClick }) => {
  return (
    <CardContent sx={sourcesPanelStyles.cardContent}>
      {/* Title section */}
      <Box sx={sourcesPanelStyles.titleSection}>
        <Box sx={sourcesPanelStyles.titleImageAndTextContainer}>
          <Typography
            variant="h6"
            component="div"
            sx={sourcesPanelStyles.sourceTitle}
          >
            {source.name}
          </Typography>
        </Box>
      </Box>

      {/* Description - Show formatted ingredients */}
      <Typography
        variant="body2"
        sx={{
          ...sourcesPanelStyles.sourceDescription,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 3,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {formatIngredients(source)}
      </Typography>

      {/* Author section */}
      <Typography variant="body2" sx={sourcesPanelStyles.sourceDescription}>
        Authored by {formatAuthorsOrContributors(source)}
      </Typography>

      {/* View source button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: "auto",
        }}
      >
        {source.url && (
          <Button
            variant="text"
            onClick={(e) => onLinkClick(e, source)}
            sx={sourcesPanelStyles.viewSourceLink}
          >
            View source
          </Button>
        )}
      </Box>
    </CardContent>
  );
};

export default MateriomFormulationCardContent;
